import { apply_markup_to_number } from '../flight-offer';
import {
  displayedAvailability,
  isBlackListedByCarrier,
  notActuallySeats,
  SEAT_SUITABLE_FOR_INFANT,
} from '../resources/seat-map';
import utilities, { is_child, is_infant } from '../utilities';

const notActuallySeatsCodes = new Set(Object.keys(notActuallySeats));

const isActualSeat = (seat) => {
  if (!seat) return false;

  const { characteristicsCodes = [] } = seat;

  if (!characteristicsCodes.length) return true;

  return !characteristicsCodes.some((code) => notActuallySeatsCodes.has(code));
};

export const filterHeldPassengers = (passengers) =>
  passengers.filter((p) => {
    if (p.travelerType) return p.travelerType !== 'HELD_INFANT';
    return p.traveler_type !== 'HELD_INFANT';
  });

export const alternateChildrenAndAdults = (passengers) => {
  const sorted = [];
  const adults = passengers.filter((p) => (p.travelerType || p.traveler_type) === 'ADULT');
  const children = passengers.filter((p) => (p.travelerType || p.traveler_type) === 'CHILD');
  let index = 0;
  while (index < Math.max(adults.length, children.length)) {
    if (index < children.length) {
      sorted.push(children[index]);
    }
    if (index < adults.length) {
      sorted.push(adults[index]);
    }
    index += 1;
  }
  return sorted;
};

const travelerTypePriority = (passenger) => {
  if (is_infant(passenger) || passenger.holding) return 2;
  if (is_child(passenger)) return 1;
  return 0;
};

export const sortPassengers = (passengers) =>
  passengers.sort((p1, p2) => travelerTypePriority(p2) - travelerTypePriority(p1));

const parseSeatNumber = (seatNumber) => ({
  row: seatNumber.substring(0, seatNumber.length - 1),
  column: seatNumber[seatNumber.length - 1],
});

export const isSeatAvailableForAnyPassenger = (seat) =>
  seat.travelerPricing.some(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );

export const isSeatAvailableForAllPassengers = (seat) =>
  seat.travelerPricing.every(
    (pricing) => pricing.seatAvailabilityStatus.toUpperCase() === 'AVAILABLE'
  );

export const isSeatAvailableForPassenger = (travelerId, seat) => {
  if (!seat) {
    return false;
  }
  const travelerPricing = (seat.travelerPricing || []).reduce(
    (pricing, current) => (current.travelerId === travelerId ? current : pricing),
    undefined
  );
  return (
    !!travelerPricing &&
    String(travelerPricing.seatAvailabilityStatus).toUpperCase() === 'AVAILABLE'
  );
};

const isSeatBlackListedByCarrier = (seat, carrier) => {
  const checker = isBlackListedByCarrier[carrier];
  return checker && checker(seat.characteristicsCodes);
};

export const mapSeatObjectsToGrid = (flightOffer, data, dictionary = {}) => {
  const aircraftMap = {};
  const columns = {};
  const rows = {};
  const priceMap = {};
  const availableSeatsRemoved = {};

  const { deckConfiguration, seats = [], facilities = [], carrierCode } = data;
  const { width, exitRowsX: exitRows = [] } = deckConfiguration;
  let hasSeatsSuitableForInfant = false;

  for (let col = 0; col < width; col += 1) {
    columns[col] = {
      name: '',
      isHallway: true,
    };
  }

  seats.forEach((seat) => {
    const { coordinates } = seat;
    const { x, y } = coordinates;
    // sanity check
    if (!aircraftMap[x]) {
      aircraftMap[x] = {
        blocks: {},
      };
    }
    // sanity check
    if (Number.isNaN(y) || Number.isNaN(x)) {
      return;
    }
    // Check if a child could seat here so that he has at least someone near
    const { row: seatRow, column: seatColumn } = parseSeatNumber(seat.number);
    const isAvailable = isSeatAvailableForAnyPassenger(seat);
    const isAvailableForAll = isSeatAvailableForAllPassengers(seat);

    aircraftMap[x].blocks[y] = {
      ...seat,
      isPartiallyAvailable: isAvailable && !isAvailableForAll,
      travelerPricing: seat.travelerPricing.map((pricing) => {
        const hasPrice = !!pricing.price;
        let seatAvailabilityStatus = pricing.seatAvailabilityStatus || 'BLOCKED';
        let blackListed = false;
        if (seatAvailabilityStatus === 'AVAILABLE') {
          // Some seats characteristics are not allowed & they are disabled.
          // Chargeable seats with no prices are disabled.
          // Some carriers blacklist some seats
          if (!isActualSeat(seat) || !hasPrice || isSeatBlackListedByCarrier(seat, carrierCode)) {
            seatAvailabilityStatus = 'BLOCKED';
            blackListed = true;
            availableSeatsRemoved[pricing.travelerId] =
              (availableSeatsRemoved[pricing.travelerId] || 0) + 1;
          }
        }

        if (!hasPrice || !pricing.price.total || !flightOffer) {
          return {
            ...pricing,
            seatAvailabilityStatus,
            blackListed,
          };
        }

        const total = pricing.price.total;
        let grandTotal = priceMap[total];
        if (!grandTotal) {
          grandTotal = apply_markup_to_number(flightOffer, Number(total));
          priceMap[total] = grandTotal;
        }

        return {
          ...pricing,
          seatAvailabilityStatus,
          blackListed,
          price: {
            ...pricing.price,
            grandTotal,
          },
        };
      }),
      row: seatRow,
      column: seatColumn,
      type: 'seat',
    };

    if (y > 0) {
      let prev = y - 1;
      let found = false;
      while (prev >= 0 && !found) {
        const seatToTheLeft = aircraftMap[x].blocks[prev];
        if (seatToTheLeft) {
          aircraftMap[x].blocks[y].left = {
            number: seatToTheLeft.number,
            isBlocked: !isSeatAvailableForAnyPassenger(seatToTheLeft),
          };
          aircraftMap[x].blocks[prev].right = {
            number: seat.number,
            isBlocked: !isAvailable,
          };
          found = true;
        } else {
          prev -= 1;
        }
      }
    }

    const characteristicsCodes = seat.characteristicsCodes || [];
    if (characteristicsCodes.includes(SEAT_SUITABLE_FOR_INFANT) && isAvailable) {
      hasSeatsSuitableForInfant = true;
    }

    columns[y] = {
      name: seatColumn,
      isHallway: false,
    };
    rows[x] = Number(seatRow);
  });

  const facilitiesDictionary = dictionary.facilities || {};
  facilities.forEach((facility, i) => {
    const { coordinates } = facility;
    const { x, y } = coordinates;
    // sanity check
    if (!aircraftMap[x]) {
      aircraftMap[x] = {
        blocks: {},
      };
    }
    // sanity check
    if (Number.isNaN(y) || Number.isNaN(x)) {
      return;
    }
    aircraftMap[x].blocks[y] = {
      ...facility,
      facilityId: [facility.position, facility.column, facility.row, facility.code, i]
        .filter((f) => !!f)
        .join('-'),
      description: facilitiesDictionary[facility.code],
      type: 'facility',
    };
  });

  exitRows.forEach((exit) => {
    if (!aircraftMap[exit]) {
      aircraftMap[exit] = {
        exits: {},
      };
    }
    const exitRow = aircraftMap[exit];
    aircraftMap[exit] = {
      ...exitRow,
      exits: {
        0: {
          type: 'exit',
          direction: 'left',
        },
        [width - 1]: {
          type: 'exit',
          direction: 'right',
        },
      },
    };
    for (let col = 1; col < width - 1; col += 1) {
      aircraftMap[exit].exits[col] = {
        type: 'empty',
      };
    }
  });

  // Fill empty spaces
  try {
    Object.keys(aircraftMap).forEach((row) => {
      const elem = aircraftMap[row] || {};
      const blocks = elem.blocks || {};
      if (Object.keys(blocks).length < width) {
        for (let col = 0; col < width; col += 1) {
          aircraftMap[row].blocks[col] = blocks[col] || {
            type: 'empty',
          };
        }
      }
    });
  } catch (e) {
    utilities.methods.log('mapSeatObjectsToGrid error');
    utilities.methods.log(e);
  }
  // Adjust hallways
  const columnKeys = Object.keys(columns);
  const adjustedColumns = { ...columns };
  if (columnKeys.length > 2) {
    for (let col = 1; col < columnKeys.length - 1; col += 1) {
      const leftIsHallway = columns[col - 1] && columns[col - 1].isHallway;
      const rightIsHallway = columns[col + 1] && columns[col + 1].isHallway;
      const column = columns[col];
      if (column.isHallway) {
        adjustedColumns[col] = {
          ...column,
          isHallway: (leftIsHallway && rightIsHallway) || (!leftIsHallway && !rightIsHallway),
        };
      }
    }
  }

  return {
    hasSeatsSuitableForInfant,
    aircraftMap,
    availableSeatsRemoved,
    columns: adjustedColumns,
    rows,
  };
};

export const getTravelerPricing = (seat, travelerId, fallbackToDefault = true) => {
  if (!seat) {
    return {};
  }

  const pricingIndex = seat.travelerPricing.findIndex(
    (pricing) => pricing.travelerId === travelerId
  );
  if (!fallbackToDefault) {
    return pricingIndex >= 0 ? seat.travelerPricing[pricingIndex] : {};
  }

  if (seat.travelerPricing && seat.travelerPricing.length) {
    return seat.travelerPricing[Math.max(0, pricingIndex)];
  }
  return {};
};

export const getSeatPrice = (seat, travelerId, markedUp = true) => {
  const { price: seatPrice } = getTravelerPricing(seat, travelerId, false);
  if (!seatPrice) {
    return 0;
  }

  let grandTotal = seatPrice.total || 0;
  if (markedUp) {
    grandTotal = seatPrice.grandTotal || seatPrice.total;
  }
  // Keep it to cents decimal
  return Math.ceil(Number(grandTotal) * 100) / 100;
};

export const getSeatsForTraveler = (flightOffer, travelerId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings.filter((t) => t.travelerId === travelerId);
    for (let i = 0; i < pricings.length; i += 1) {
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment;
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            seats.push({
              segmentId: fare.segmentId,
              number: fare.additionalServices.chargeableSeatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    utilities.methods.log('getSeatsForTraveler error');
    utilities.methods.log(e);
  }
  return seats;
};

export const getSeatsForSegment = (flightOffer, segmentId) => {
  const seats = [];
  try {
    const pricings = flightOffer.travelerPricings;
    for (let i = 0; i < pricings.length; i += 1) {
      const travelerId = pricings[i].travelerId;
      const fareDetailsBySegment = pricings[i].fareDetailsBySegment.filter(
        (fare) => fare.segmentId === segmentId
      );
      if (fareDetailsBySegment) {
        for (let j = 0; j < fareDetailsBySegment.length; j += 1) {
          const fare = fareDetailsBySegment[j];
          if (fare.additionalServices && fare.additionalServices.chargeableSeatNumber) {
            seats.push({
              travelerId,
              number: fare.additionalServices.chargeableSeatNumber,
            });
          }
        }
      }
    }
  } catch (e) {
    utilities.methods.log('getSeatsForSegment error');
    utilities.methods.log(e);
  }
  return seats;
};

export const getSegmentsFromFlightOffers = (flightOffers) => {
  const segmentsMap = {};
  const allSegments = flightOffers.flatMap((offer) =>
    offer.itineraries.flatMap((itinerary) => itinerary.segments)
  );
  for (let index = 0; index < allSegments.length; index++) {
    const segment = allSegments[index];
    segmentsMap[segment.id] = segment;
  }
  return segmentsMap;
};

export const getDisplayAvailability = (availability) => {
  const key = (availability || '').toLowerCase();
  return displayedAvailability[key];
};
